import dayjs from 'dayjs';

import { FULL_DATETIME_FORMAT } from 'core/constants';

import { formatFloating, formatMoment, isNaiveDateTime } from 'utils/formatters/timezone';

export type PartnerValue = string | number | boolean | null | PartnerValue[] | { [key: string]: PartnerValue };

export interface PartnerRow {
  label: string;
  text: string;
}

const TRAILING_ID = /(^|[_\s-])id$/i;
const CAMEL_ID = /[a-z](Id|ID)$/;

const isIdentifier = (key: string): boolean => TRAILING_ID.test(key) || CAMEL_ID.test(key);

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATETIME = /^\d{4}-\d{2}-\d{2}T([01]\d|2[0-3]):[0-5]\d/;

export const isObject = (value: PartnerValue): value is { [key: string]: PartnerValue } =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const tidyLabel = (key: string): string => {
  const words = key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase();

  return words.charAt(0).toUpperCase() + words.slice(1);
};

// A partner's date-time with no offset is its wall-clock, as the entity PDF prints it
const formatPartnerDateTime = (value: string): string =>
  isNaiveDateTime(value)
    ? formatFloating(value, 'date', FULL_DATETIME_FORMAT)
    : formatMoment(value, FULL_DATETIME_FORMAT);

const asDate = (value: string, format: (date: string) => string): string => {
  const day = value.slice(0, 10);

  return dayjs(day).format('YYYY-MM-DD') === day ? format(value) : value;
};

const formatScalar = (value: string | number | boolean): string => {
  if (typeof value !== 'string') {
    return String(value);
  }
  if (ISO_DATE.test(value)) {
    return asDate(value, (date) => formatFloating(date, 'date'));
  }
  if (ISO_DATETIME.test(value)) {
    return asDate(value, formatPartnerDateTime);
  }
  return value;
};

const meaningful = (value: { [key: string]: PartnerValue }): [string, PartnerValue][] => {
  const entries = Object.entries(value).filter(([, part]) => part !== null && part !== undefined && part !== '');
  const named = entries.filter(([key]) => !isIdentifier(key));

  return named.length > 0 ? named : entries;
};

export const asText = (value: PartnerValue): string | null => {
  if (value === null || value === undefined) {
    return null;
  }
  if (!isObject(value) && !Array.isArray(value)) {
    return formatScalar(value);
  }
  if (Array.isArray(value)) {
    const items = value.map(asText);
    return items.every((item) => item !== null) ? items.join(', ') : null;
  }

  const parts = meaningful(value);
  return parts.length === 1 ? asText(parts[0][1]) : null;
};

const rowsFor = (value: PartnerValue, path: string[]): PartnerRow[] => {
  const text = asText(value);
  if (text !== null) {
    return [{ label: path.join(' — '), text }];
  }
  if (Array.isArray(value)) {
    return value.flatMap((entry, index) =>
      rowsFor(entry, [...path.slice(0, -1), `${path[path.length - 1]} ${index + 1}`])
    );
  }

  if (!isObject(value)) {
    return [];
  }

  return meaningful(value).flatMap(([key, part]) => rowsFor(part, [...path, tidyLabel(key)]));
};

export const asRows = (value: { [key: string]: PartnerValue }): PartnerRow[] =>
  meaningful(value).flatMap(([key, part]) => rowsFor(part, [tidyLabel(key)]));
